import type { BillingPrefs } from '@/store/slices/userPreferences';

/**
 * Per-country bank-payout field rules. Which details a credit transfer needs
 * differs by country: the ~88 IBAN countries use an IBAN; others use a local
 * clearing identifier (US routing, UK sort code, India IFSC, Australia BSB,
 * Canada institution+transit, Mexico CLABE, etc.) plus an account number; and
 * SWIFT/BIC is the global standard for the inbound cross-border leg. This module
 * resolves the ordered list of fields (with labels + validators) for a country;
 * the form renders exactly those, and the validator checks exactly those.
 *
 * Data verified against the ISO 13616 IBAN registry (SWIFT), iban.com, and Wise /
 * central-bank payout guides. Countries not modelled explicitly fall back to
 * "account number + SWIFT", which is the universal international-transfer minimum.
 */

/** A bank-detail field that can appear in the form; each is a key on BillingPrefs. */
export type BankFieldKey =
  | 'iban'
  | 'bicSwift'
  | 'accountNumber'
  | 'routingNumber'
  | 'sortCode'
  | 'ifsc'
  | 'bsb'
  | 'institutionNumber'
  | 'transitNumber'
  | 'clabe'
  | 'bankCode'
  | 'branchCode'
  | 'cnapsCode'
  | 'bankAddress'
  | 'intermediaryBankSwift'
  | 'intermediaryBankAccount';

export type BankFieldSpec = {
  key: BankFieldKey;
  label: string;
  required: boolean;
  placeholder?: string;
  numeric?: boolean;
  /** Returns false for a malformed NON-EMPTY value (emptiness is handled by `required`). */
  validate?: (value: string) => boolean;
};

/* ---------- validators ---------- */

const norm = (value: string): string => value.replace(/\s+/g, '').toUpperCase();
const digits = (value: string): string => value.replace(/\D/g, '');
const hasDigits = (value: string, count: number): boolean => digits(value).length === count;

/** IBAN format + ISO 7064 mod-97 checksum. */
export const isValidIban = (value: string): boolean => {
  const iban = norm(value);
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban)) return false;
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  const numeric = rearranged.replace(/[A-Z]/g, (ch) => String(ch.charCodeAt(0) - 55));
  let remainder = 0;
  for (let i = 0; i < numeric.length; i += 1) remainder = (remainder * 10 + Number(numeric[i])) % 97;
  return remainder === 1;
};

/** SWIFT/BIC (ISO 9362): 6 letters + 2 alphanumerics + optional 3-char branch. */
export const isValidBic = (value: string): boolean => /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(norm(value));

/** US ABA routing number: 9 digits with the standard weighted checksum. */
const isValidRouting = (value: string): boolean => {
  const d = digits(value);
  if (d.length !== 9) return false;
  const n = d.split('').map(Number);
  return (3 * (n[0] + n[3] + n[6]) + 7 * (n[1] + n[4] + n[7]) + (n[2] + n[5] + n[8])) % 10 === 0;
};

/** India IFSC: 4 bank letters, a 0, then a 6-char branch code (11 total). */
const isValidIfsc = (value: string): boolean => /^[A-Z]{4}0[A-Z0-9]{6}$/.test(norm(value));

/** Mexico CLABE: exactly 18 digits. */
const isValidClabe = (value: string): boolean => /^\d{18}$/.test(digits(value));

/* ---------- IBAN countries (ISO 13616 registry, verified Dec-2024 + MN 2025) ---------- */

export const IBAN_COUNTRIES: ReadonlySet<string> = new Set([
  'AD', 'AE', 'AL', 'AT', 'AZ', 'BA', 'BE', 'BG', 'BH', 'BI', 'BR', 'BY', 'CH', 'CR', 'CY', 'CZ',
  'DE', 'DJ', 'DK', 'DO', 'EE', 'EG', 'ES', 'FI', 'FK', 'FO', 'FR', 'GB', 'GE', 'GI', 'GL', 'GR',
  'GT', 'HN', 'HR', 'HU', 'IE', 'IL', 'IQ', 'IS', 'IT', 'JO', 'KW', 'KZ', 'LB', 'LC', 'LI', 'LT',
  'LU', 'LV', 'LY', 'MC', 'MD', 'ME', 'MK', 'MN', 'MR', 'MT', 'MU', 'NI', 'NL', 'NO', 'OM', 'PK',
  'PL', 'PS', 'PT', 'QA', 'RO', 'RS', 'RU', 'SA', 'SC', 'SD', 'SE', 'SI', 'SK', 'SM', 'SO', 'ST',
  'SV', 'TN', 'TR', 'UA', 'VA', 'VG', 'XK', 'YE',
]);

/* ---------- field metadata ---------- */

type FieldMeta = Omit<BankFieldSpec, 'key' | 'required'>;

const FIELD_META: Record<BankFieldKey, FieldMeta> = {
  iban: { label: 'IBAN', placeholder: 'e.g. DE89 3704 0044 0532 0130 00', validate: isValidIban },
  bicSwift: { label: 'SWIFT / BIC code', placeholder: 'e.g. DEUTDEFF', validate: isValidBic },
  accountNumber: { label: 'Account number' },
  routingNumber: { label: 'Routing number (ABA)', numeric: true, placeholder: '9 digits', validate: isValidRouting },
  sortCode: { label: 'Sort code', numeric: true, placeholder: '00-00-00', validate: (v) => hasDigits(v, 6) },
  ifsc: { label: 'IFSC code', placeholder: 'e.g. HDFC0001234', validate: isValidIfsc },
  bsb: { label: 'BSB (Bank-State-Branch)', numeric: true, placeholder: '000-000', validate: (v) => hasDigits(v, 6) },
  institutionNumber: { label: 'Institution number', numeric: true, placeholder: '3 digits', validate: (v) => hasDigits(v, 3) },
  transitNumber: { label: 'Branch transit number', numeric: true, placeholder: '5 digits', validate: (v) => hasDigits(v, 5) },
  clabe: { label: 'CLABE', numeric: true, placeholder: '18 digits', validate: isValidClabe },
  bankCode: { label: 'Bank code' },
  branchCode: { label: 'Branch code' },
  cnapsCode: { label: 'CNAPS code', numeric: true, placeholder: '12 digits', validate: (v) => hasDigits(v, 12) },
  // International-wire extras (non-SEPA corridors).
  bankAddress: { label: 'Bank address', placeholder: 'Branch / head-office address' },
  intermediaryBankSwift: {
    label: 'Intermediary bank SWIFT / BIC',
    placeholder: 'e.g. CHASUS33',
    validate: isValidBic,
  },
  intermediaryBankAccount: { label: 'Intermediary bank account / reference' },
};

/* ---------- SEPA (Single Euro Payments Area) participants ----------
   A SEPA-zone payout needs only IBAN + BIC in EUR; anything else is an
   international wire that additionally needs the bank's address and, on some
   corridors, an intermediary/correspondent bank. This is the EPC's scheme
   geographical scope as ISO 3166-1 alpha-2 IBAN-prefix codes (EU 27 + EEA + the
   non-EEA participants), which lines up with `IBAN_COUNTRIES` above (territories
   like French overseas departments route under their parent's IBAN prefix).
   Verified Dec-2025 against the official EPC list (EPC409-09 v8.0); the
   five most-recent non-EEA admissions (AL/MD/ME/MK/RS) are operationally live by
   their 2025-2026 readiness dates. */
export const SEPA_COUNTRIES: ReadonlySet<string> = new Set([
  // EU 27
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV',
  'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  // EEA (non-EU)
  'IS', 'LI', 'NO',
  // Non-EEA participants
  'CH', 'GB', 'MC', 'SM', 'AD', 'VA', 'GI', 'AL', 'MD', 'ME', 'MK', 'RS',
]);

/** Whether a country is in the SEPA zone (EU/IBAN corridor). */
export const isSepaCountry = (country: string): boolean => SEPA_COUNTRIES.has(country);

/* ---------- per-country rules (non-IBAN, or IBAN countries with a familiar local
   scheme like the UK / Brazil). `swift` is whether a SWIFT/BIC is required for the
   inbound international leg. `labels` overrides the generic field label locally. ---------- */

type CountryRule = {
  fields: BankFieldKey[];
  swift: 'required' | 'optional';
  labels?: Partial<Record<BankFieldKey, string>>;
};

const COUNTRY_RULES: Record<string, CountryRule> = {
  US: { fields: ['routingNumber', 'accountNumber'], swift: 'optional' },
  CA: { fields: ['institutionNumber', 'transitNumber', 'accountNumber'], swift: 'optional' },
  AU: { fields: ['bsb', 'accountNumber'], swift: 'optional' },
  NZ: { fields: ['accountNumber'], swift: 'required' },
  IN: { fields: ['ifsc', 'accountNumber'], swift: 'required' },
  CN: { fields: ['accountNumber', 'cnapsCode'], swift: 'required' },
  JP: { fields: ['bankCode', 'branchCode', 'accountNumber'], swift: 'required' },
  KR: { fields: ['bankCode', 'accountNumber'], swift: 'required' },
  BR: {
    fields: ['bankCode', 'branchCode', 'accountNumber'],
    swift: 'required',
    labels: { bankCode: 'Bank code (código do banco)', branchCode: 'Agência (branch)' },
  },
  MX: { fields: ['clabe'], swift: 'optional' },
  ZA: { fields: ['branchCode', 'accountNumber'], swift: 'required' },
  NG: { fields: ['bankCode', 'accountNumber'], swift: 'required', labels: { accountNumber: 'NUBAN account number' } },
  KE: { fields: ['bankCode', 'branchCode', 'accountNumber'], swift: 'required' },
  HK: { fields: ['bankCode', 'branchCode', 'accountNumber'], swift: 'required' },
  SG: { fields: ['bankCode', 'branchCode', 'accountNumber'], swift: 'required' },
  PH: { fields: ['bankCode', 'accountNumber'], swift: 'required' },
  ID: { fields: ['bankCode', 'accountNumber'], swift: 'required' },
  TH: { fields: ['bankCode', 'accountNumber'], swift: 'required' },
  MY: { fields: ['bankCode', 'accountNumber'], swift: 'required' },
  GB: { fields: ['sortCode', 'accountNumber'], swift: 'optional' },
  RU: { fields: ['bankCode', 'accountNumber'], swift: 'required', labels: { bankCode: 'BIK (bank code)' } },
};

/**
 * The ordered bank-detail fields to collect for a given country (ISO alpha-2).
 * Empty array until a country is chosen.
 *
 * Resolution has two layers:
 *  1. Account identifier + SWIFT/BIC. An explicit local rule wins (incl. UK sort
 *     code / Brazil agência even though they also have IBANs); otherwise an IBAN
 *     country uses IBAN; otherwise the universal fallback is an account number.
 *     A SWIFT/BIC is required for SEPA (EU) and for every international wire.
 *  2. SEPA (EU/IBAN) corridors need nothing more. NON-SEPA international wires
 *     additionally need the bank's address (required) and, on some corridors, an
 *     intermediary/correspondent bank (optional).
 */
export const bankFieldsForCountry = (country: string): BankFieldSpec[] => {
  if (!country) return [];

  const make = (key: BankFieldKey, required: boolean, labelOverride?: string): BankFieldSpec => ({
    key,
    required,
    ...FIELD_META[key],
    ...(labelOverride ? { label: labelOverride } : {}),
  });

  const sepa = SEPA_COUNTRIES.has(country);
  const specs: BankFieldSpec[] = [];

  const rule = COUNTRY_RULES[country];
  if (rule) {
    rule.fields.forEach((key) => specs.push(make(key, true, rule.labels?.[key])));
    // Within SEPA, respect the local rule's SWIFT need; an international wire
    // always needs it.
    specs.push(make('bicSwift', sepa ? rule.swift === 'required' : true));
  } else if (IBAN_COUNTRIES.has(country)) {
    specs.push(make('iban', true));
    specs.push(make('bicSwift', true));
  } else {
    specs.push(make('accountNumber', true));
    specs.push(make('bicSwift', true));
  }

  if (!sepa) {
    specs.push(make('bankAddress', true));
    specs.push(make('intermediaryBankSwift', false));
    specs.push(make('intermediaryBankAccount', false));
  }

  return specs;
};
